import React from 'react';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import isNode from '../is-node';
import { RCCloseButton } from '../rc/RCCloseButton';

import bind from '../bind';

const MODAL_OUTLET = '.modal-outlet__dialogs';

export type Size = 'wide' | 'narrow' | 'fluid';

interface IModalProps {
  id?: string;
  subClass?: string;
  contentLabel?: string;
  isOpen: boolean;
  onRequestClose?: Function;
  shouldCloseOnOverlayClick?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  paddedBody?: boolean;
  newClose?: boolean;
  sidebarContent?: JSX.Element | React.ReactNode;
  size?: Size;
}

export default class Modal extends React.Component<IModalProps, {}> {
  constructor(props) {
    super(props);

    // Element to add `aria-hidden` to when modal is open. This should really
    // be a child of body, but our layout doesn't have a single container element.
    // https://reactcommunity.org/react-modal/examples/set_app_element.html
    ReactModal.setAppElement('body');
  }

  @bind
  outlet() {
    return document.querySelector(MODAL_OUTLET) || document.body;
  }

  @bind
  handleClose(evt) {
    evt.stopPropagation();
    evt.preventDefault();

    this.props.onRequestClose();
  }

  @bind
  handleAfterOpen() {
    /* eslint-disable no-unused-expressions */
    // Hack for Microsoft Edge - for some reason, on this browser the modal is
    // not being painted on the screen when opened. This will force a repaint of
    // the parent element of the modal. This issue was not reproducable from a
    // basic ReactModal installation, so it's unclear what the root cause is.
    const els = document.getElementsByClassName('ReactModalPortal');
    for (let i = 0; i < els.length; i += 1) {
      const el = els[i] as HTMLElement;
      el.style.display = 'none';
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      el.style.display = '';
    }
    /* eslint-enable no-unused-expressions */
  }

  renderCloseButton() {
    if (!this.props.onRequestClose) { return null; }

    return (
      <div className="reverb-modal__close">
        <RCCloseButton onClick={this.handleClose} />
      </div>
    );
  }

  renderHeader() {
    if (!this.props.headerTitle) { return null; }

    return (
      <div className="reverb-modal__header">
        <h3 className="reverb-modal__header__title">
          {this.props.headerTitle}
        </h3>
        {this.props.headerSubtitle &&
          <p className="reverb-modal__header__subtitle">
            {this.props.headerSubtitle}
          </p>
        }
      </div>
    );
  }

  renderSidebar() {
    if (!this.props.sidebarContent) {
      return null;
    }

    return (
      <div className="reverb-modal__sidebar">
        {this.props.sidebarContent}
      </div>
    );
  }

  render() {
    // https://reactjs.org/docs/error-decoder.html/?invariant=257
    if (isNode) { return null; }

    const classes = classNames(
      'reverb-modal',
      { 'reverb-modal--padded-body': this.props.paddedBody },
      { 'reverb-modal--new-close': this.props.newClose },
      { 'reverb-modal--with-sidebar': this.props.sidebarContent },
      { 'reverb-modal--wide': this.props.size === 'wide' },
      { 'reverb-modal--narrow': this.props.size === 'narrow' },
      { 'reverb-modal--fluid': this.props.size === 'fluid' },
      this.props.subClass && `reverb-modal--${this.props.subClass}`,
    );

    return (
      // https://github.com/reactjs/react-modal/pull/734
      // This code means that ALL clicks from inside the modal will bubble up a click.
      // This causes any onClick handlers above this to understand _how_ to handle these
      // events. In this case a CoreLink wrapped the modal and caused it to route
      <ReactModal
        id={this.props.id}
        isOpen={this.props.isOpen}
        contentLabel="Modal"
        overlayClassName="reverb-modal__container"
        className={classes}
        shouldCloseOnOverlayClick={this.props.shouldCloseOnOverlayClick}
        onRequestClose={this.handleClose}
        parentSelector={this.outlet}
        onAfterOpen={this.handleAfterOpen}
      >
        <div
          className="reverb-modal__content"
          onClick={(e) => { e.stopPropagation(); }}
        >
          {this.renderHeader()}
          {this.renderSidebar()}
          <div className="reverb-modal__body">
            {this.props.children}
          </div>
          {this.renderCloseButton()}
        </div>
      </ReactModal>
    );
  }
}
